<template>
  <div class="payflow-desktop">
    <div
      class="payflow-desktop__grid"
      :class="{ 'payflow-desktop__grid--full': !showPaymentPanel }"
    >
      <!-- right side -->
      <section class="payflow-desktop__invoice">
        <div id="print" class="payflow-invoice-body">
          <header class="payflow-invoice-header payflow-invoice-header--in-print">
            <div class="payflow-invoice-header__brand">
              <div class="payflow-invoice-header__logo-wrap">
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  alt="logo"
                  class="payflow-invoice-header__logo"
                />
                <div v-else class="payflow-invoice-header__logo-placeholder">
                  <v-icon color="#B0BEC5" size="28">mdi-store-outline</v-icon>
                </div>
              </div>
              <div class="payflow-invoice-header__shop">
                <h1 class="payflow-invoice-header__shop-name">{{ shopName }}</h1>
                <p v-if="shopSubtitle" class="payflow-invoice-header__shop-sub">
                  {{ shopSubtitle }}
                </p>
              </div>
            </div>
            <div class="payflow-invoice-header__meta">
              <div class="payflow-invoice-header__number">
                {{ $t("number") }}: {{ displayNumber }}
              </div>
              <div class="payflow-invoice-header__date">{{ $t("date") }}: {{ date }}</div>
            </div>
          </header>

          <div class="payflow-customer-block payflow-customer-block--in-print">
            <p class="payflow-invoice-greeting">
              <template v-if="customerDisplayName">
                {{ $t("DearCustomer") }}
                <strong class="payflow-customer-name">{{ customerDisplayName }}</strong
                >،
                {{ $t("thanksToBuy") }}
              </template>
              <template v-else>
                {{ $t("DearCustomer") }} ، {{ $t("thanksToBuy") }}
              </template>
            </p>
            <div
              v-if="customer && (customer.phone || customer.mobile || customer.address)"
              class="payflow-customer-meta"
            >
              <div v-if="customer.phone" class="payflow-customer-meta__row">
                <v-icon x-small color="#90A4AE" class="ml-1">mdi-phone-outline</v-icon>
                <span class="payflow-customer-meta__label">{{ $t("phone") }}:</span>
                <span class="payflow-customer-meta__value">{{ customer.phone }}</span>
              </div>
              <div v-if="customer.mobile" class="payflow-customer-meta__row">
                <v-icon x-small color="#90A4AE" class="ml-1">mdi-cellphone</v-icon>
                <span class="payflow-customer-meta__label">{{ $t("mobile") }}:</span>
                <span class="payflow-customer-meta__value">{{ customer.mobile }}</span>
              </div>
              <div v-if="customer.address" class="payflow-customer-meta__row">
                <v-icon x-small color="#90A4AE" class="ml-1"
                  >mdi-map-marker-outline</v-icon
                >
                <span class="payflow-customer-meta__label">{{ $t("address") }}:</span>
                <span class="payflow-customer-meta__value">{{ customer.address }}</span>
              </div>
            </div>
          </div>

          <v-data-table
            v-if="headers && items"
            ref="table"
            :headers="headers"
            :items="items"
            class="invoice-print-table payflow-table payflow-invoice-table"
            :items-per-page="8000000000000"
            hide-default-footer
          >
            <template v-slot:item.unit="{ item }">
              {{ item.product && item.product.unit ? item.product.unit.fame : "" }}
            </template>
            <template v-slot:item.product="{ item }">
              <span class="payflow-cell-product">{{ item.product.name }}</span>
            </template>
            <template v-slot:item.count="{ item }">
              {{ $numberFormat(parseFloat(item.count)) }}
            </template>
            <template v-slot:item.fee="{ item }">
              {{ $numberFormat(parseFloat(item.fee)) }}
            </template>
            <template v-slot:item.row="{ item }">
              {{ parseInt(items.indexOf(item)) + 1 }}
            </template>
            <template v-slot:item.tax="{ item }">
              {{ $numberFormat(parseFloat(item.tax)) }}
            </template>
            <template v-slot:item.discount="{ item }">
              <span v-if="parseFloat(item.discount) > 0" class="payflow-discount-text">
                {{ $numberFormat(parseFloat(item.discount)) }}
              </span>
              <span v-else>—</span>
            </template>
            <template v-slot:item.sum="{ item }">
              {{
                $numberFormat(
                  parseFloat(item.fee * item.count) +
                    parseFloat(item.tax) -
                    parseFloat(item.discount)
                )
              }}
            </template>
            <template v-slot:body.append>
              <tr class="payflow-tfoot-row">
                <td colspan="6" class="payflow-tfoot-label">{{ $t("sum") }}</td>
                <td colspan="2" class="payflow-tfoot-value">
                  {{ $numberFormat(totals.fee) }} {{ currencyName }}
                </td>
              </tr>
              <tr class="payflow-tfoot-row">
                <td colspan="6" class="payflow-tfoot-label">
                  {{ $t("tax") }}
                  <span v-if="taxPercent > 0">({{ taxPercent }}٪)</span>
                </td>
                <td colspan="2" class="payflow-tfoot-value">
                  {{ $numberFormat(totals.tax) }} {{ currencyName }}
                </td>
              </tr>
              <tr v-if="totalDiscount > 0" class="payflow-tfoot-row">
                <td colspan="6" class="payflow-tfoot-label">{{ $t("discount") }}</td>
                <td colspan="2" class="payflow-tfoot-value payflow-discount-text">
                  {{ $numberFormat(totalDiscount) }}- {{ currencyName }}
                </td>
              </tr>
              <tr class="payflow-tfoot-row payflow-tfoot-row--total">
                <td colspan="6" class="payflow-tfoot-label payflow-tfoot-label--bold">
                  {{ $t("amountForPay") }}
                </td>
                <td colspan="2" class="payflow-tfoot-value payflow-tfoot-value--total">
                  {{ $numberFormat(grandTotal) }} {{ currencyName }}
                </td>
              </tr>
              <tr v-if="content" class="invoice-content-row">
                <td colspan="8">
                  <span class="invoice-content-label">{{ $t("content") }}:</span>
                  {{ content }}
                </td>
              </tr>
            </template>
          </v-data-table>

          <footer class="payflow-invoice-note">
            <v-icon small color="#90A4AE" class="ml-1">mdi-information-outline</v-icon>
            {{ $t("payflowSystemInvoiceNote") }}
          </footer>
        </div>
      </section>

      <!-- left side -->
      <aside v-if="showPaymentPanel" class="payflow-desktop__checkout">
        <div class="payflow-sidebar-card">
          <template v-if="needsPayment">
            <h2 class="payflow-sidebar-heading">
              {{ $t("summery_peyment") }}
            </h2>

            <div class="payflow-sidebar-amount-block">
              <span class="payflow-sidebar-amount-label">{{ $t("amountForPay") }}</span>
              <div class="payflow-sidebar-amount">
                {{ $numberFormat(remainingAmount) }}
              </div>
              <div class="payflow-sidebar-currency">{{ currencyName }}</div>
            </div>

            <div class="payflow-sidebar-divider" />

            <div class="payflow-sidebar-breakdown">
              <div class="payflow-breakdown-row">
                <span>{{ $t("sum") }}</span>
                <span>{{ $numberFormat(totals.fee || 0) }} {{ currencyName }}</span>
              </div>
              <div class="payflow-breakdown-row">
                <span>
                  {{ $t("tax") }}
                  <template v-if="taxPercent > 0">({{ taxPercent }}٪)</template>
                </span>
                <span>{{ $numberFormat(totals.tax || 0) }} {{ currencyName }}</span>
              </div>
              <div v-if="totalDiscount > 0" class="payflow-breakdown-row">
                <span>{{ $t("discount") }}</span>
                <span class="payflow-discount-text">
                  {{ $numberFormat(totalDiscount) }}- {{ currencyName }}
                </span>
              </div>
            </div>

            <div class="payflow-sidebar-divider" />

            <div class="payflow-gateway-block">
              <h3 class="payflow-gateway-heading">
                {{ $t("payflowSelectGateway") }}
              </h3>
              <div
                v-for="gw in availableGateways"
                :key="gw.value"
                :class="[
                  'payflow-gateway-option',
                  { 'payflow-gateway-option--active': gateway === gw.value },
                ]"
                @click="$emit('select-gateway', gw.value)"
              >
                <div class="payflow-gateway-option__radio">
                  <span
                    :class="[
                      'payflow-radio',
                      { 'payflow-radio--checked': gateway === gw.value },
                    ]"
                  />
                </div>
                <div class="payflow-gateway-option__info">
                  <span class="payflow-gateway-option__name">{{ gw.name }}</span>
                  <span class="payflow-gateway-option__en">{{ gw.englishName }}</span>
                </div>
                <div class="payflow-gateway-option__logo">
                  <img :src="gw.logo" :alt="gw.englishName" />
                </div>
              </div>
            </div>

            <v-btn
              v-shortkey="['enter']"
              :disabled="!gateway"
              :loading="loading"
              block
              large
              color="#0066FF"
              class="payflow-pay-btn white--text"
              @shortkey="$emit('pay')"
              @click="$emit('pay')"
            >
              {{ $t("payment") }}
            </v-btn>

            <div class="payflow-sidebar-footer">
              <div v-if="qrCodeUrl" class="payflow-sidebar-qr">
                <img :src="qrCodeUrl" width="88" height="88" alt="QR" />
              </div>
              <div class="payflow-sidebar-actions">
                <button type="button" class="payflow-text-btn" @click="$emit('print')">
                  <v-icon small color="#546E7A" class="ml-1">mdi-file-pdf-box</v-icon>
                  {{ $t("download") }} PDF
                </button>
                <button type="button" class="payflow-text-btn" @click="$emit('share')">
                  <v-icon small color="#546E7A" class="ml-1">mdi-share-variant</v-icon>
                  {{ $t("shareInvoice") }}
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="isFullyPaid">
            <div class="payflow-paid-state">
              <v-icon color="#43A047" size="56" class="mb-3"
                >mdi-check-circle-outline</v-icon
              >
              <p class="payflow-paid-state__title">{{ $t("cleared") }}</p>
              <p class="payflow-paid-state__subtitle">{{ $t("invoicePayedDone") }}</p>
              <div class="payflow-sidebar-footer payflow-sidebar-footer--center">
                <div v-if="qrCodeUrl" class="payflow-sidebar-qr">
                  <img :src="qrCodeUrl" width="88" height="88" alt="QR" />
                </div>
                <div class="payflow-sidebar-actions">
                  <button type="button" class="payflow-text-btn" @click="$emit('print')">
                    <v-icon small color="#546E7A" class="ml-1">mdi-file-pdf-box</v-icon>
                    {{ $t("download") }} PDF
                  </button>
                  <button type="button" class="payflow-text-btn" @click="$emit('share')">
                    <v-icon small color="#546E7A" class="ml-1">mdi-share-variant</v-icon>
                    {{ $t("shareInvoice") }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
export default {
  name: "PayFlowDesktop",
  props: {
    headers: { type: Array, default: null },
    items: { type: Array, default: () => [] },
    content: { type: String, default: null },
    currencyName: { type: String, default: "" },
    totals: { type: Object, required: true },
    totalDiscount: { type: Number, default: 0 },
    remainingAmount: { type: Number, default: 0 },
    availableGateways: { type: Array, default: () => [] },
    gateway: { type: String, default: null },
    loading: { type: Boolean, default: false },
    needsPayment: { type: Boolean, default: false },
    isFullyPaid: { type: Boolean, default: false },
    showPaymentPanel: { type: Boolean, default: false },
    qrCodeUrl: { type: String, default: null },
    self: { type: Object, default: null },
    customer: { type: Object, default: null },
    date: { type: String, default: "" },
    displayNumber: { type: String, default: "" },
    shopName: { type: String, default: "" },
  },
  computed: {
    logoUrl() {
      return this.self && this.self.logo ? this.self.logo.link : null;
    },
    shopSubtitle() {
      if (!this.self) return "";
      return this.self.address || this.self.phone || "";
    },
    customerDisplayName() {
      if (!this.customer || !this.customer.name) return "";
      const gender = this.customer.gender ? this.$t(this.customer.gender) + " " : "";
      return gender + this.customer.name;
    },
    grandTotal() {
      return this.totals.fee - this.totalDiscount + this.totals.tax;
    },
    taxPercent() {
      if (!this.totals.fee || this.totals.fee <= 0) return 0;
      return Math.round((this.totals.tax / this.totals.fee) * 100);
    },
  },
};
</script>

<style scoped>
.payflow-desktop {
  min-height: 100vh;
  background: #ffffff;
}

.payflow-desktop__grid {
  display: flex;
  align-items: stretch;
  min-height: 100vh;
  width: 100%;
}

.payflow-desktop__grid--full {
  justify-content: center;
}

.payflow-desktop__invoice {
  flex: 1 1 auto;
  min-width: 0;
  background: #ffffff;
  padding: 36px 48px 48px;
}

.payflow-desktop__grid--full .payflow-desktop__invoice {
  flex: 0 1 960px;
  width: 100%;
}

.payflow-invoice-body {
  max-width: 820px;
  margin: 0 auto;
}

.payflow-desktop__checkout {
  flex: 0 0 400px;
  width: 400px;
  min-height: 100vh;
  background: #eef1f6;
  border-inline-start: 1px solid #dde3ea;
  padding: 32px 28px 40px;
  box-sizing: border-box;
}

.payflow-sidebar-card {
  position: sticky;
  top: 32px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e4e9f0;
  border-radius: 16px;
  padding: 24px 22px;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.07), 0 2px 8px rgba(15, 23, 42, 0.04);
}

.payflow-invoice-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.payflow-invoice-header__brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.payflow-invoice-header__logo-wrap {
  flex-shrink: 0;
}

.payflow-invoice-header__logo {
  width: 52px;
  height: 52px;
  object-fit: contain;
  border-radius: 10px;
}

.payflow-invoice-header__logo-placeholder {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: #f5f7fa;
  border: 1px dashed #cfd8dc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payflow-invoice-header__shop-name {
  font-size: 18px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
  line-height: 1.3;
}

.payflow-invoice-header__shop-sub {
  font-size: 12px;
  color: #90a4ae;
  margin: 0;
  line-height: 1.4;
}

.payflow-invoice-header__meta {
  text-align: left;
  flex-shrink: 0;
}

.payflow-invoice-header__number {
  font-size: 14px;
  font-weight: 600;
  color: #0066ff;
  margin-bottom: 4px;
}

.payflow-invoice-header__date {
  font-size: 13px;
  color: #78909c;
}

.payflow-invoice-greeting {
  font-size: 13px;
  color: #546e7a;
  margin: 0;
  line-height: 1.6;
}

.payflow-customer-block {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef1f6;
}

.payflow-customer-name {
  font-weight: 700;
  color: #212121;
  font-size: 14px;
}

.payflow-customer-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.payflow-customer-meta__row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #78909c;
}

.payflow-customer-meta__label {
  flex-shrink: 0;
  font-weight: 500;
  color: #90a4ae;
}

.payflow-customer-meta__value {
  color: #455a64;
  font-weight: 500;
}

.payflow-invoice-table {
  border: 1px solid #e8ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 20px;
}

.payflow-invoice-note {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #90a4ae;
  margin: 0;
  padding-top: 4px;
}

.payflow-discount-text {
  color: #e53935 !important;
  font-weight: 500;
}

.payflow-cell-product {
  font-weight: 500;
  color: #37474f;
}

.payflow-sidebar-heading {
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  text-align: center;
  margin: 0 0 16px;
}

.payflow-sidebar-amount-block {
  text-align: center;
  margin-bottom: 4px;
}

.payflow-sidebar-amount-label {
  display: block;
  font-size: 12px;
  color: #90a4ae;
  margin-bottom: 6px;
}

.payflow-sidebar-amount {
  font-size: 34px;
  font-weight: 800;
  color: #0066ff;
  letter-spacing: -0.5px;
  line-height: 1.15;
  margin-top: 4px;
}

.payflow-sidebar-currency {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #0066ff;
  margin-top: 4px;
}

.payflow-sidebar-divider {
  height: 1px;
  background: #f0f2f5;
  margin: 16px 0;
}

.payflow-sidebar-breakdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payflow-breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #78909c;
}

.payflow-breakdown-row span:last-child {
  font-weight: 600;
  color: #455a64;
}

.payflow-gateway-block {
  margin-bottom: 20px;
}

.payflow-gateway-heading {
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
  margin: 0 0 12px;
}

.payflow-gateway-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  background: #fff;
}

.payflow-gateway-option:last-child {
  margin-bottom: 0;
}

.payflow-gateway-option:hover {
  border-color: #b3d1ff;
}

.payflow-gateway-option--active {
  border: 2px solid #0066ff;
  background: #f5f9ff;
  box-shadow: 0 0 0 1px rgba(0, 102, 255, 0.08);
}

.payflow-gateway-option__radio {
  flex-shrink: 0;
}

.payflow-radio {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cfd8dc;
  background: #fff;
  position: relative;
  transition: border-color 0.2s;
}

.payflow-radio--checked {
  border-color: #0066ff;
}

.payflow-radio--checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #0066ff;
}

.payflow-gateway-option__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.payflow-gateway-option__name {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
}

.payflow-gateway-option__en {
  font-size: 11px;
  color: #90a4ae;
  text-transform: capitalize;
}

.payflow-gateway-option__logo {
  flex-shrink: 0;
  width: 48px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payflow-gateway-option__logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.payflow-pay-btn {
  border-radius: 12px !important;
  height: 48px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
  margin-bottom: 20px;
}

.payflow-sidebar-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid #f0f2f5;
}

.payflow-sidebar-footer--center {
  flex-direction: column;
  align-items: center;
  border-top: none;
  padding-top: 16px;
}

.payflow-sidebar-qr img {
  display: block;
  border-radius: 6px;
  border: 1px solid #e8ecef;
}

.payflow-sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.payflow-text-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #546e7a;
  background: #fff;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  white-space: nowrap;
}

.payflow-text-btn:hover {
  border-color: #0066ff;
  background: #f5f9ff;
  color: #0066ff;
}

.payflow-paid-state {
  text-align: center;
  padding: 12px 0 0;
}

.payflow-paid-state__title {
  font-size: 18px;
  font-weight: 700;
  color: #43a047;
  margin: 0 0 4px;
}

.payflow-paid-state__subtitle {
  font-size: 13px;
  color: #78909c;
  margin: 0 0 8px;
}

.invoice-content-label {
  font-weight: 600;
  color: #616161;
}
</style>

<style lang="sass">
@import '~vuetify/src/styles/tools/_rtl.sass'

.payflow-table.invoice-print-table.theme--light.v-data-table
  border: none
  border-radius: 0
  background: transparent
  box-shadow: none

  .v-data-table__wrapper
    overflow: visible !important

    table
      border-collapse: separate
      border-spacing: 0

      thead tr.payflow-header-row
        display: none

      thead tr:last-child:not(.payflow-header-row) th
        background: #f5f7fa !important
        font-weight: 600 !important
        color: #546e7a !important
        font-size: 12px !important
        border: none !important
        border-bottom: 1px solid #e8ecef !important
        padding: 10px 12px !important
        white-space: nowrap

      thead tr:last-child th:first-child
        border-radius: 0 8px 0 0

      thead tr:last-child th:last-child
        border-radius: 8px 0 0 0

      tbody tr:not(.payflow-tfoot-row):not(.invoice-content-row) td
        border: none !important
        border-bottom: 1px solid #f0f2f5 !important
        padding: 11px 12px !important
        font-size: 13px
        color: #455a64
        text-align: center

      tbody tr:not(.payflow-tfoot-row):not(.invoice-content-row) td:nth-child(3)
        display: none

      thead tr:last-child th:nth-child(3)
        display: none

      tbody tr.payflow-tfoot-row td
        border: none !important
        padding: 8px 12px !important
        font-size: 13px
        background: transparent !important

      tbody tr.payflow-tfoot-row .payflow-tfoot-label
        text-align: right
        color: #78909c
        font-weight: 500

      tbody tr.payflow-tfoot-row .payflow-tfoot-label--bold
        font-weight: 700
        color: #37474f

      tbody tr.payflow-tfoot-row .payflow-tfoot-value
        text-align: left
        font-weight: 600
        color: #455a64

      tbody tr.payflow-tfoot-row--total td
        background: #f0f6ff !important
        padding: 12px !important
        border-top: 1px solid #cce0ff !important

      tbody tr.payflow-tfoot-row--total .payflow-tfoot-value--total
        color: #0066ff !important
        font-size: 15px !important
        font-weight: 700 !important

      tbody tr.payflow-tfoot-row--total td:first-child
        border-radius: 0 0 8px 0

      tbody tr.payflow-tfoot-row--total td:last-child
        border-radius: 0 0 0 8px

      tbody tr.invoice-content-row td
        background: #fafafa !important
        border-top: 1px solid #e8ecef !important
        padding: 10px 12px !important
        font-size: 12px
        color: #546e7a
</style>
